/*
 * Reine Bausteine der Einführungstouren (AAR-1993): Layout-Band aus der
 * Fensterbreite, Schritte je Band auflösen, Gate-Ergebnis deuten. Ohne Vue und
 * DOM, damit sie per `npm run test:unit` prüfbar sind.
 *
 * Ein Band ist der Bereich zwischen zwei Kanten, an denen sich eine Tour
 * unterscheidet (Anker, Popover-Seite, Text, Vorbereitung). Die Kanten kommen
 * aus denselben Quellen wie die Komponenten (Vuetify-Thresholds, Konstante der
 * Belegprüfung) — nie als eigene Zahl im Tour-Code.
 */

/** Ein Band ab `min` px (einschließlich); das erste Band beginnt bei 0. */
export type BandSpec<B extends string = string> = readonly { band: B; min: number }[]

/** Band zur Breite. Die Spezifikation ist aufsteigend nach `min` sortiert. */
export function bandFor<B extends string>(width: number, spec: BandSpec<B>): B {
	const [first] = spec
	if (!first) throw new Error('bandFor: leere Band-Spezifikation')
	let current = first.band
	for (const entry of spec) {
		if (width >= entry.min) current = entry.band
	}
	return current
}

/** Warum ein Schritt nicht gezeigt wurde. */
export type TourSkipReason =
	| 'layout' // gibt es in diesem Band nicht
	| 'permission' // kein Abo, keine Rolle, Mandanten-Einstellung
	| 'data' // keine Daten (z. B. keine Zähler)
	| 'error' // technischer Fehler (API, Vorbereitung)
	| 'anchor' // Element fehlt oder ist versteckt
	| 'covered' // Element liegt unter einer festen Leiste

export type TourLogEntry = {
	id: string
	status: 'shown' | 'skipped'
	reason?: TourSkipReason
	detail?: string
}

/** Ausnahmen, die ein Gate oder eine Prüfung beim Anzeigen melden darf. */
export type TourSkip = { skip: 'permission' | 'data' }

/** `true` = zeigen, `false` = nicht berechtigt, sonst Grund. Fehler werden geworfen. */
export type GateResult = boolean | TourSkip

export function gateOutcome(
	result: GateResult
): { show: true } | { show: false; reason: 'permission' | 'data' } {
	if (result === true) return { show: true }
	if (result === false) return { show: false, reason: 'permission' }
	return { show: false, reason: result.skip }
}

/** Was ein Schritt je Band abweichend haben darf. */
export type BandAware<S> = S & {
	/** Schritt nur in diesen Bändern; fehlt = alle Bänder des Strangs. */
	bands?: readonly string[]
	/** Abweichungen je Band, flach über den Schritt gelegt. */
	variants?: Partial<Record<string, Partial<S>>>
}

/**
 * Schritte für ein Band: fremde Bänder fallen mit Grund `layout` heraus,
 * Varianten werden übernommen. Die Felder `bands`/`variants` bleiben am
 * Ergebnis, stören aber niemanden.
 */
export function resolveSteps<S extends { id: string }>(
	steps: readonly BandAware<S>[],
	band: string
): { steps: BandAware<S>[]; skipped: TourLogEntry[] } {
	const out: BandAware<S>[] = []
	const skipped: TourLogEntry[] = []
	for (const step of steps) {
		if (step.bands && !step.bands.includes(band)) {
			skipped.push({ id: step.id, status: 'skipped', reason: 'layout' })
			continue
		}
		const variant = step.variants?.[band]
		out.push(variant ? { ...step, ...variant } : step)
	}
	return { steps: out, skipped }
}

/** Ein Lauf gilt als unvollständig, sobald ein Schritt technisch scheiterte. */
export function isIncomplete(log: readonly TourLogEntry[]): boolean {
	return log.some((entry) => entry.reason === 'error')
}
